import { useEffect, useRef, useState, type ReactNode } from 'react'
import AgencyMark from '../marks/AgencyMark'
import { displayFonts, sansFonts } from '@/content/fonts'
import { america, findMission, themeFor } from '@/content/missions'
import { contrastRatio } from '@/content/color'
import { markStyles, usePrefs } from '@/state/prefs'
import { usePinnedTrack } from '@/state/usePinnedTrack'
import { markStyleInfo } from '@/content/marks'
import Cased from '../ui/Cased'
import { useCustomRoles } from '@/state/useCustomRoles'
import { usePalette } from '@/state/usePalette'
import { loadFont } from '@/state/fontLoaders'
import { deriveTheme } from '@/content/theme'

function Panel({ index, eyebrow, title, children }: { index: number; eyebrow: string; title: string; children: ReactNode }) {
  return (
    <article className="tour-panel" data-panel={index} aria-labelledby={`tour-${index}`}>
      <div className="tour-panel__head">
        <span className="mds-eyebrow">{eyebrow}</span>
        <h3 id={`tour-${index}`} className="mds-h2">
          <Cased>{title}</Cased>
        </h3>
      </div>
      <div className="tour-panel__body">{children}</div>
    </article>
  )
}

/** One sample per choice: the heading face and the body face, each beside its options. */
function TypePanel() {
  const { prefs, set } = usePrefs()
  const panel = useRef<HTMLDivElement>(null)
  // Each option is set in its own face, so every face loads as the panel
  // nears the screen rather than with the page.
  useEffect(() => {
    const loadAll = () => {
      for (const font of [...displayFonts, ...sansFonts]) loadFont(font.id)
    }
    if (!panel.current || typeof IntersectionObserver === 'undefined') return loadAll()
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return
        observer.disconnect()
        loadAll()
      },
      { rootMargin: '600px 0px' },
    )
    observer.observe(panel.current)
    return () => observer.disconnect()
  }, [])
  const display = displayFonts.find((font) => font.id === prefs.fontDisplay) ?? displayFonts[0]
  const body = sansFonts.find((font) => font.id === prefs.fontSans) ?? sansFonts[0]
  const rows = [
    { kind: 'display', font: display, list: displayFonts, value: prefs.fontDisplay, key: 'fontDisplay', label: 'Display typeface' },
    { kind: 'body', font: body, list: sansFonts, value: prefs.fontSans, key: 'fontSans', label: 'Body typeface' },
  ] as const
  return (
    <div className="tour-type" ref={panel}>
      {rows.map((row) => (
        <div key={row.kind} className={`tour-type__row tour-type__row--${row.kind}`}>
          <p className="tour-type__specimen" aria-hidden="true">
            Aa
          </p>
          <div className="tour-type__meta">
            <p className="mds-h3">{row.font.name}</p>
            <p className="mds-muted">{row.font.note}</p>
            <div className="mds-segmented" role="group" aria-label={row.label}>
              {row.list.map((font) => (
                <button
                  key={font.id}
                  type="button"
                  className="mds-segmented__option"
                  style={{ fontFamily: font.stack }}
                  aria-pressed={row.value === font.id}
                  onClick={() => set(row.key, font.id)}
                >
                  {font.name}
                </button>
              ))}
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}

/** A swatch's hex value, editable in place: type a value and press Enter. */
function HexField({ value, label, onCommit }: { value: string; label: string; onCommit: (hex: string) => boolean }) {
  const [draft, setDraft] = useState<string | null>(null)
  // An invalid value simply reverts to the current color.
  const commit = () => {
    if (draft !== null) onCommit(draft.trim())
    setDraft(null)
  }
  return (
    <input
      className="tour-color__hex"
      aria-label={`${label} hex value`}
      value={draft ?? value}
      spellCheck={false}
      maxLength={7}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={commit}
      onKeyDown={(event) => {
        if (event.key === 'Enter') event.currentTarget.blur()
        if (event.key === 'Escape') {
          setDraft(null)
          event.currentTarget.blur()
        }
      }}
    />
  )
}

/**
 * The theme's roles. Primary, secondary and accent are the three source
 * colors, editable with a color picker or by typing a hex value; the rest are
 * derived from them.
 */
function ColorPanel() {
  const { prefs } = usePrefs()
  const mission = findMission(prefs.mission) ?? america
  const { colors, update } = usePalette()
  const [primary, secondary, accent] = colors
  const theme = prefs.palette ? deriveTheme({ primary, secondary, accent }) : themeFor(mission)
  const set = theme[prefs.theme]
  const swatches = [
    { role: 'primary', label: 'Primary', source: 0, text: set['on-primary'], against: set.primary },
    { role: 'ink', label: 'Secondary', source: 1, text: set.ink, against: set.bg },
    { role: 'accent', label: 'Accent', source: 2, text: set['on-accent'], against: set.accent },
    { role: 'surface', label: 'Surface', source: null, text: set.ink, against: set.surface },
    { role: 'link', label: 'Link', source: null, text: set.link, against: set.bg },
  ] as const
  return (
    <div className="tour-color">
      <ul className="tour-color__swatches">
        {swatches.map((swatch) => {
          const value = set[swatch.role]
          const ratio = contrastRatio(swatch.text, swatch.against)
          const editable = swatch.source !== null
          return (
            <li key={swatch.role} className="mds-swatch">
              {editable ? (
                <label className="mds-swatch__chip tour-color__pick" style={{ background: value }}>
                  <input
                    type="color"
                    value={colors[swatch.source]}
                    onChange={(event) => update(swatch.source, event.target.value)}
                  />
                  <span className="mds-visually-hidden">Choose the {swatch.label.toLowerCase()} color</span>
                </label>
              ) : (
                <span className="mds-swatch__chip" style={{ background: value }} />
              )}
              <span className="tour-color__name">{swatch.label}</span>
              {editable ? (
                <HexField value={colors[swatch.source]} label={swatch.label} onCommit={(hex) => update(swatch.source, hex)} />
              ) : (
                <code>{value}</code>
              )}
              <span className="mds-muted tour-color__ratio">
                {editable ? `${ratio}:1 with text` : `Derived, ${ratio}:1`}
              </span>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

/** Both modes side by side; choosing one switches the whole site. */
function ModesPanel() {
  const { prefs, set } = usePrefs()
  const mission = findMission(prefs.mission) ?? america
  const customRoles = useCustomRoles()
  return (
    <div className="tour-color">
      <div className="tour-color__modes" role="group" aria-label="Color mode">
        {(['light', 'dark'] as const).map((mode) => (
          <button
            key={mode}
            type="button"
            className="tour-mini tour-mini--choice"
            data-theme={mode}
            data-mission={mission.id}
            style={customRoles(mode)}
            aria-pressed={prefs.theme === mode}
            onClick={() => set('theme', mode)}
          >
            <span className="mds-eyebrow">{mode}</span>
            <span className="tour-mini__title">{mission.short === 'USA' ? 'Hello, America' : mission.name}</span>
            <span className="tour-mini__actions" aria-hidden="true">
              <span className="mds-button mds-button--primary mds-button--sm">Primary</span>
              <span className="mds-tag mds-tag--accent">Accent</span>
            </span>
            <span className="mds-visually-hidden">, use {mode} mode</span>
          </button>
        ))}
      </div>
    </div>
  )
}

function MarksPanel() {
  const { prefs, set } = usePrefs()
  const mission = findMission(prefs.mission) ?? america
  return (
    <div className="tour-marks">
      <div className="tour-marks__row" role="group" aria-label="Mark style">
        {markStyles.map((style) => {
          const option = { label: markStyleInfo[style].name }
          return (
            <button key={style} type="button" className="tour-marks__option" aria-pressed={prefs.mark === style} onClick={() => set('mark', style)}>
              <span className="tour-marks__art">
                <AgencyMark mission={mission} variant={style} />
              </span>
              <span>{option.label}</span>
            </button>
          )
        })}
      </div>
      <p className="mds-muted">
        Every mission has six marks: its flag, seal and logo, simple and pixel emblems drawn from its seal, and the
        flag in motion. Choose a mission to see its set.
      </p>
    </div>
  )
}

const panels = [
  { eyebrow: '01 Typography', title: 'A font with a voice.', Body: TypePanel },
  { eyebrow: '02 Color', title: 'Red, white and blue. Or yours.', Body: ColorPanel },
  { eyebrow: '03 Light and dark', title: 'Day shift. Night shift.', Body: ModesPanel },
  { eyebrow: '04 Marks', title: 'Flag, seal, logo and more.', Body: MarksPanel },
]

export default function ChoicesTour() {
  const { section, track, pinned, onFocus } = usePinnedTrack(panels.length)
  return (
    <section
      ref={section}
      className={`tour${pinned ? ' tour--pinned' : ''}`}
      style={pinned ? { height: `${panels.length * 85 + 40}vh` } : undefined}
      aria-labelledby="tour-title"
      onFocus={onFocus}
    >
      <div className="tour__sticky">
        <div className="mds-container tour__head">
          <span className="tour__eyebrow">
            <span className="mds-eyebrow">Every choice, live</span>
          </span>
          <h2 id="tour-title" className="mds-display">
            <Cased>Change anything. Watch the system follow.</Cased>
          </h2>
        </div>
        <div className="tour__viewport">
          <div ref={track} className="tour__track">
            {panels.map(({ eyebrow, title, Body }, index) => (
              <Panel key={eyebrow} index={index} eyebrow={eyebrow} title={title}>
                <Body />
              </Panel>
            ))}
          </div>
        </div>
        <div className="tour__progress" aria-hidden="true">
          <span />
        </div>
      </div>
    </section>
  )
}
